<template>
  <div class="mural-card">
    <div class="flex items-center justify-between mb-1" style="gap: 1rem;">
      <h4 class="mb-0 mural-side-title">{{ $t('mural.sales.title') }}</h4>
      <b-badge variant="light-primary" pill>{{ periodLabel }}</b-badge>
    </div>

    <div class="flex flex-column" style="gap: .9rem;">
      <div v-for="sale in rows" :key="sale.order_id" class="mural-sale">
        <span class="mural-sale__avatar" :style="sale.avatarStyle">{{ sale.initials }}</span>
        <div class="mural-sale__main">
          <div class="mural-sale__name">{{ sale.buyer }}</div>
          <div class="flex items-center" style="gap: .4rem; min-width: 0;">
            <b-badge :variant="sale.isPlan ? 'light-primary' : 'light-warning'" pill class="mural-sale__tag">
              {{ sale.type_label }}
            </b-badge>
            <span class="mural-sale__item">{{ sale.item }}</span>
          </div>
        </div>
        <div class="mural-sale__value">
          <div class="font-weight-bold hb-money">{{ money(sale.value) }}</div>
          <div class="text-muted"><small>{{ sale.when }}</small></div>
        </div>
      </div>

      <div v-if="!rows.length" class="text-center text-muted py-3">
        <small>{{ $t('mural.sales.empty') }}</small>
      </div>
    </div>

    <div class="mural-sale__total">
      <span>{{ $t('mural.sales.total') }}</span>
      <strong class="hb-money">{{ money(total) }}</strong>
    </div>
  </div>
</template>

<script>
import { BBadge } from 'bootstrap-vue'
import { currencyFormatter } from '@/util/Util'

const AVATAR_TONES = [
  ['var(--tint-primary)', '#5f87b3'],
  ['var(--tint-success)', '#1d9153'],
  ['var(--tint-warning)', '#c67728'],
  ['var(--tint-info)', '#0794a8'],
  ['var(--tint-danger)', '#c9484a'],
]

/**
 * Últimas vendas do período, pelo valor líquido do estabelecimento.
 *
 * O total do rodapé é o mesmo número do KPI de receita, de propósito: dois
 * valores discordando na mesma tela fazem o usuário desconfiar dos dois.
 */
export default {
  name: 'MuralSales',
  components: { BBadge },
  props: {
    sales: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    periodLabel: { type: String, default: '' },
  },
  computed: {
    rows() {
      return this.sales.map(sale => {
        const [background, color] = this.toneOf(sale.buyer || '')
        return {
          ...sale,
          isPlan: sale.type === 'SPL',
          initials: this.initialsOf(sale.buyer || ''),
          avatarStyle: { background, color },
          when: this.whenLabel(sale.created_at),
        }
      })
    },
  },
  methods: {
    money(value) {
      return currencyFormatter(Number(value || 0))
    },
    initialsOf(name) {
      const parts = String(name).trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '--'
      const first = parts[0][0]
      const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return `${first}${last}`.toUpperCase()
    },
    /** Tom estável por nome: a mesma pessoa mantém a cor entre recarregamentos. */
    toneOf(name) {
      let hash = 0
      for (let i = 0; i < name.length; i++) {
        hash = (hash * 31 + name.charCodeAt(i)) % 997
      }
      return AVATAR_TONES[hash % AVATAR_TONES.length]
    },
    whenLabel(value) {
      if (!value) return ''
      const date = new Date(String(value).replace(' ', 'T'))
      if (Number.isNaN(date.getTime())) return ''

      const today = new Date()
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)

      const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
      const sameDay = (a, b) => a.toDateString() === b.toDateString()

      if (sameDay(date, today)) return `Hoje · ${time}`
      if (sameDay(date, yesterday)) return `Ontem · ${time}`

      return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')} · ${time}`
    },
  },
}
</script>

<style lang="scss" scoped>
.mural-card {
  background: var(--surface-card);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-card);
  padding: 1.25rem;
}

.mural-side-title {
  font-size: 1.07rem;
  font-weight: var(--fw-semibold);
  color: var(--text-heading);
}

.mural-sale {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &__avatar {
    width: 34px;
    height: 34px;
    flex: none;
    border-radius: var(--radius-pill);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: var(--fw-semibold);
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 0.875rem;
    font-weight: var(--fw-semibold);
    color: var(--text-heading);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__tag {
    flex: none;
    font-size: 0.65rem !important;
  }

  &__item {
    font-size: 0.75rem;
    color: var(--text-body);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value {
    flex: none;
    text-align: right;
    font-size: 0.875rem;
  }

  &__total {
    border-top: 1px solid var(--border-color);
    margin-top: 1rem;
    padding-top: 0.8rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.8rem;
    color: var(--text-body);
  }
}
</style>
